// OGC-531 Creatives Manager preview video with its own controls (no browser
// media chrome): a centred play/pause button, mute and an elapsed/total time
// with a progress bar. Keyboard: the buttons take Enter/Space.
import { useEffect, useRef, useState } from 'react';
import ProtectedVideo from '../../components/ProtectedVideo.jsx';

function clock(seconds) {
  const value = Number.isFinite(seconds) && seconds > 0 ? Math.floor(seconds) : 0;
  return `${Math.floor(value / 60)}:${String(value % 60).padStart(2, '0')}`;
}

export default function CatalogVideo({ src, poster = '', label = 'Creative video', onDimensions }) {
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [time, setTime] = useState({ current: 0, duration: 0 });
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setPlaying(false);
    setFailed(false);
    setTime({ current: 0, duration: 0 });
  }, [src]);

  function toggle() {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      const started = video.play();
      if (started?.catch) started.catch(() => setPlaying(false));
    } else {
      video.pause();
    }
  }

  const progress = time.duration ? Math.min(100, (time.current / time.duration) * 100) : 0;
  return (
    <div className={`cm-video${playing ? ' is-playing' : ''}`}>
      <ProtectedVideo
        ref={videoRef}
        src={src}
        poster={poster || undefined}
        playsInline
        preload="metadata"
        muted={muted}
        aria-label={label}
        onClick={toggle}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
        onError={() => setFailed(true)}
        onLoadedMetadata={(event) => {
          const video = event.currentTarget;
          setTime({ current: video.currentTime, duration: video.duration });
          onDimensions?.(video.videoWidth, video.videoHeight);
        }}
        onTimeUpdate={(event) => setTime({ current: event.currentTarget.currentTime, duration: event.currentTarget.duration })}
      />
      {failed ? <span className="cm-video-status" role="status">Video temporarily unavailable.</span> : null}
      <button type="button" className="cm-video-play" aria-label={`${playing ? 'Pause' : 'Play'} ${label}`} onClick={toggle} disabled={failed}>
        <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor" aria-hidden="true" focusable="false">
          {playing ? <path d="M7 5h3.5v14H7zM13.5 5H17v14h-3.5z" /> : <path d="m8 5 11 7-11 7Z" />}
        </svg>
      </button>
      <div className="cm-video-bar">
        <button type="button" className="cm-video-mute" aria-pressed={muted ? 'true' : 'false'} aria-label={muted ? 'Unmute video' : 'Mute video'} onClick={() => setMuted((value) => !value)} disabled={failed}>
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
            <path d="M4 9v6h4l5 4V5L8 9Z" />
            {muted ? <path d="m17 9 4 6m0-6-4 6" /> : <path d="M16.5 8.5a5 5 0 0 1 0 7M19 6a8.5 8.5 0 0 1 0 12" />}
          </svg>
        </button>
        <div className="cm-video-progress" role="progressbar" aria-label="Playback progress" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(progress)}>
          <span style={{ width: `${progress}%` }} />
        </div>
        <span className="cm-video-time">{clock(time.current)} / {clock(time.duration)}</span>
      </div>
    </div>
  );
}
